/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useMemo, useState } from "react";
import { List, ListItemButton, ListItemIcon, ListItemText, Collapse } from "@mui/material";
import SiteGroupList from "./SiteGroups/SiteGroupList";
import { Box } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import "../../pages/Configuration/Config.css";
import { setBasicDialogOpenFor, setErrorMsg, setSelectedNavListItems } from "../../store/helpers/helperReducers";
import BasicDialog from "../Shared/Dialogs/BasicDialog";
import { DIALOGOPENFOR } from "../../globals/globals";
import { useNodeLeftSiteNav } from "./hooks";
import { InputField } from "../Shared/Radix/Inputs/InputField";
import { Sheet } from "../ui/sheet";
import { Button as ShadButton } from "../ui/button";
import { ISiteGroup } from "../../store/api/devices/types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LanIcon from "@mui/icons-material/Lan";
import { useSelector } from "react-redux";
import { selectedGeoRegionIdsSelector } from "../../store/api/admin/selectors";
import { SidebarGroup, SidebarGroupLabel, SidebarGroupContent } from "../../components/ui/sidebar";
import { confirmDialogSelector, selectedNavListItemSelector } from "../../store/helpers/helperSelectors";
import { setSelectedGeoRegion } from "../../store/api/admin/reducers";
import { setRegionFilterIds, setSelectedDeviceSiteGroup } from "../../store/filters/Nodes/reducer";
import { setRegionFilterIds as setInventoryRegionFilterIds } from "../../store/filters/Inventory/reducer";
import ConfirmDialog from "../Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../globals/constants";

const NodeLeftSideNavigation: React.FC = () => {
  const {
    permissions,
    dispatch,
    currentSiteGroups,
    filterGroupList,
    isEnableSave,
    basicDialogOpenFor,
    addSiteGroupHandler,
    handleOnChange,
    submitHandler,
    selectedGeoRegion,
    selectedSiteGroup,
    setSelectedSiteGroup,
    deleteHandler,
    handleDeleteAction,
    ungroupDeviceList,
    siteGroupFormData,
    setSiteGroupFormData,
    geoRegions,
    openAddSiteGroupDialogForRegion,
    nodesCounts,
  } = useNodeLeftSiteNav();

  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  const selectedNavItem = useSelector(selectedNavListItemSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const [expandedRegions, setExpandedRegions] = useState<{ [key: string]: boolean }>({});

  const filterHeaderTitle = useMemo(() => {
    if (!geoRegions || !geoRegions.length) {
      return "";
    }

    const allKeys = geoRegions.map((region) => region.objectKey);

    // Single-region deployments: just show that region's name.
    if (allKeys.length === 1) {
      return geoRegions[0].name;
    }

    const effectiveKeys = selectedGeoRegionIds && selectedGeoRegionIds.length ? selectedGeoRegionIds : allKeys;

    if (effectiveKeys.length === 1 && geoRegions.find((region) => region.objectKey === effectiveKeys[0])) {
      return geoRegions.find((region) => region.objectKey === effectiveKeys[0])?.name || "";
    }

    if (effectiveKeys.length === allKeys.length) {
      return "All Regions";
    }

    return "Multiregion (" + effectiveKeys.length + ")";
  }, [geoRegions, selectedGeoRegionIds]);

  const handleSheetClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
    dispatch(setErrorMsg({}));
    setSiteGroupFormData((prev: any) => ({ ...prev, name: "" }));
  };

  const footer = (
    <>
      <ShadButton variant="outline" onClick={handleSheetClose}>
        Cancel
      </ShadButton>
      <ShadButton disabled={!isEnableSave} onClick={submitHandler}>
        Save
      </ShadButton>
    </>
  );
  // Memoize list of site groups
  const memoizedFilterGroupList = useMemo(
    () => (
      <SidebarGroup>
        {filterHeaderTitle && <SidebarGroupLabel>{filterHeaderTitle}</SidebarGroupLabel>}
        <SidebarGroupContent>
          <SiteGroupList siteGroups={filterGroupList} permissions={permissions} onDelete={deleteHandler} />
        </SidebarGroupContent>
      </SidebarGroup>
    ),
    [filterGroupList, permissions, filterHeaderTitle, deleteHandler]
  );
  const regionCountsById = useMemo(() => {
    const entries = nodesCounts?.regions?.map((region) => [region.geoRegionId, region.deviceCount]) || [];
    return new Map<string, number>(entries as Array<[string, number]>);
  }, [nodesCounts?.regions]);

  const siteGroupCountsById = useMemo(() => {
    const entries = nodesCounts?.siteGroups?.map((group) => [group.siteGroupId, group.deviceCount]) || [];
    return new Map<string, number>(entries as Array<[string, number]>);
  }, [nodesCounts?.siteGroups]);

  const siteGroupsByRegion = useMemo(() => {
    const byRegion: { [regionId: string]: ISiteGroup[] } = {};

    // Determine which regions to show
    const allRegionKeys = (geoRegions || []).map((region) => region.objectKey);
    const effectiveRegionIds = (selectedGeoRegionIds && selectedGeoRegionIds.length)
      ? selectedGeoRegionIds
      : allRegionKeys;

    // Initialize all active regions with empty arrays
    effectiveRegionIds.forEach((regionId: string | number) => {
      byRegion[regionId] = [];
    });

    // Populate with site groups
    (currentSiteGroups || []).forEach((group: ISiteGroup) => {
      const regionId = group.parentKey || "";
      if (!regionId) return;
      // Only add if this region is in the effective list
      if (byRegion[regionId] !== undefined) {
        const deviceCount = siteGroupCountsById.get(group.objectKey) ?? group.deviceCount ?? 0;
        byRegion[regionId].push({ ...group, deviceCount });
      }
    });

    return byRegion;
  }, [currentSiteGroups, geoRegions, selectedGeoRegionIds, siteGroupCountsById]);

  const selectedRegionForHighlight = useMemo(() => {
    const filterKeys = new Set([
      ...filterGroupList.map((item) => item.objectKey),
      ...ungroupDeviceList.map((item) => item.objectKey),
    ]);

    if (typeof selectedNavItem === 'string' && (filterKeys.has(selectedNavItem) || selectedNavItem.startsWith("region:"))) {
      return selectedNavItem.startsWith("region:") ? selectedNavItem.substring(7) : "";
    }

    if (typeof selectedNavItem === 'string') {
      const matchingGroup = (currentSiteGroups || []).find((group: ISiteGroup) => group.objectKey === selectedNavItem);
      if (matchingGroup?.parentKey) {
        return matchingGroup.parentKey;
      }
    }

    return selectedGeoRegion?.objectKey || "";
  }, [filterGroupList, ungroupDeviceList, selectedNavItem, currentSiteGroups, selectedGeoRegion?.objectKey]);

  const memoizedCurrentSiteGroups = useMemo(
    () => (
      <List disablePadding sx={{ width: "100%" }}>
        {Object.entries(siteGroupsByRegion)
          .filter(([regionId]) => {
            // Skip regions that don't exist in geoRegions (e.g., deleted regions)
            return geoRegions?.find((region) => region.objectKey === regionId);
          })
          .sort(([regionIdA], [regionIdB]) => {
            const regionNameA = geoRegions?.find((region) => region.objectKey === regionIdA)?.name || "";
            const regionNameB = geoRegions?.find((region) => region.objectKey === regionIdB)?.name || "";
            return regionNameA.localeCompare(regionNameB);
          })
          .map(([regionId, groups]) => {
            const regionName = geoRegions?.find((region) => region.objectKey === regionId)?.name || "";
            const regionCount = regionCountsById.get(regionId) ?? 0;
            const sortedGroups = [...groups].sort((a, b) => a.name.localeCompare(b.name));
            const isExpanded = expandedRegions[regionId] ?? (selectedGeoRegion?.objectKey === regionId);

            return (
              <Box key={regionId} sx={{ width: "100%" }}>
                <ListItemButton
                  selected={selectedRegionForHighlight === regionId}
                  onClick={() => {
                    const region = geoRegions?.find((rg) => rg.objectKey === regionId);
                    if (!region) return;

                    const isSelectedRegion =
                      selectedNavItem === `region:${regionId}` &&
                      selectedGeoRegion?.objectKey === regionId;

                    // Clicking an already-selected expanded region collapses it,
                    // but keeps the region selected.
                    if (isSelectedRegion && isExpanded) {
                      dispatch(setSelectedGeoRegion(region));
                      dispatch(setSelectedNavListItems(`region:${region.objectKey}`));
                      dispatch(setRegionFilterIds([region.objectKey]));
                      dispatch(setInventoryRegionFilterIds([region.objectKey]));
                      dispatch(setSelectedDeviceSiteGroup(""));
                      setExpandedRegions((prev) => ({ ...prev, [regionId]: false }));
                      return;
                    }

                    // All other clicks select the region and make sure it is expanded.
                    dispatch(setSelectedGeoRegion(region));
                    dispatch(setSelectedNavListItems(`region:${region.objectKey}`));
                    dispatch(setRegionFilterIds([region.objectKey]));
                    dispatch(setInventoryRegionFilterIds([region.objectKey]));
                    dispatch(setSelectedDeviceSiteGroup(""));
                    setExpandedRegions((prev) => ({ ...prev, [regionId]: true }));
                  }}
                  sx={{
                    px: 0,
                    py: 0.25,
                    minHeight: 32,
                    borderRadius: 1,
                    color: 'inherit',
                    '&:hover': { backgroundColor: 'var(--sidebar-accent)' },
                    '&.Mui-selected': {
                      backgroundColor: 'var(--sidebar-accent) !important',
                      borderRadius: 'calc(var(--radius) - 2px)',
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                    <LanIcon sx={{ fontSize: 18, color: 'inherit' }} />
                  </Box>
                  <ListItemText
                    primary={regionName.length > 20 ? regionName.slice(0, 20) + "..." : regionName}
                    title={regionName}
                    primaryTypographyProps={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  />
                  <span style={{ marginLeft: 4, fontSize: 'var(--font-size-xs)', fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{regionCount}</span>
                  {isExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  )}
                </ListItemButton>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 3.5, mt: 0 }}>
                    <SiteGroupList siteGroups={sortedGroups} permissions={permissions} showDelete onDelete={deleteHandler} />
                    {permissions?.hasOpCodeGroup("manage_topology") && (
                      <ListItemButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openAddSiteGroupDialogForRegion(regionId);
                        }}
                        sx={{
                          pl: 0.5,
                          py: 0.25,
                          minHeight: 28,
                          borderRadius: 1,
                          color: 'inherit',
                          opacity: 0.5,
                          '&:hover': { backgroundColor: 'var(--sidebar-accent)', opacity: 1 },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '26px', color: 'inherit' }}>
                          <AddIcon sx={{ fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Add group"
                          primaryTypographyProps={{ fontSize: 'var(--font-size-sm)' }}
                        />
                      </ListItemButton>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
      </List>
    ),
    [geoRegions, openAddSiteGroupDialogForRegion, permissions, selectedGeoRegion?.objectKey, selectedNavItem, siteGroupsByRegion, expandedRegions, regionCountsById, selectedRegionForHighlight, deleteHandler]
  );

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.SITEGROUPS && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteAction}>
          Are you sure you want to delete <strong>{selectedSiteGroup?.name || "Unknown"}</strong>?
        </ConfirmDialog>
      )}

      {basicDialogOpenFor === DIALOGOPENFOR.SITEGROUPS && (
        <Sheet open={true} onClose={handleSheetClose} title="Add Device Group" footer={footer} width="420px">
          <form autoComplete="off">
            <div className="space-y-5 pt-2">
              <InputField
                label="Device Group Name"
                name="name"
                required={true}
                value={siteGroupFormData?.name || ""}
                onChange={(name, value) => handleOnChange({ preventDefault: () => { }, target: { name, value } })}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.code === "Enter") {
                    event.preventDefault();
                    submitHandler();
                  }
                }}
                autoFocus
                placeholder="Enter device group name"
              />
            </div>
          </form>
        </Sheet>
      )}

      <SidebarGroup>
        <SidebarGroupContent>
          <SiteGroupList siteGroups={ungroupDeviceList} permissions={permissions} onDelete={deleteHandler} />
        </SidebarGroupContent>
      </SidebarGroup>

      {memoizedFilterGroupList}

      {/*<SiteGroupList deviceGroups={data} headerTitle="Provisioning" /> */}
      {/*<SiteGroupList deviceGroups={maintenanceList1} headerTitle="Maintenance" /> */}

      <SidebarGroup>
        <SidebarGroupLabel>Device Groups</SidebarGroupLabel>
        <SidebarGroupContent>
          {memoizedCurrentSiteGroups}
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
};

export default NodeLeftSideNavigation;
